import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";

const DEFAULT_EXERCISES = ["Bench Press", "Squat", "Deadlift", "Overhead Press", "Barbell Row", "Incline Bench", "Front Squat", "Romanian Deadlift", "Pull-Up", "Dip"];

export default function StrengthTab({ clientId, user }) {
  const [logs, setLogs] = useState([]);
  const [exercise, setExercise] = useState("Bench Press");
  const [customEx, setCustomEx] = useState("");
  const [form, setForm] = useState({ weight_lbs: "", reps: "", sets: "1", date: new Date().toISOString().slice(0,10) });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/clients/${clientId}/strength-logs`); setLogs(data); }
    catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const exercises = useMemo(() => {
    const set = new Set([...DEFAULT_EXERCISES, ...logs.map(l => l.exercise)]);
    return Array.from(set);
  }, [logs]);

  const filtered = useMemo(() => logs.filter(l => l.exercise === exercise).map(l => ({
    date: l.date,
    weight: l.weight_lbs,
    e1rm: l.estimated_1rm,
    reps: l.reps,
  })), [logs, exercise]);

  const bestE1rm = filtered.reduce((m, x) => Math.max(m, x.e1rm), 0);

  const submit = async (e) => {
    e.preventDefault();
    const ex = customEx.trim() || exercise;
    if (!ex || !form.weight_lbs || !form.reps) return toast.error("Fill exercise, weight, reps");
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/strength-logs`, {
        exercise: ex, weight_lbs: Number(form.weight_lbs), reps: Number(form.reps),
        sets: Number(form.sets) || 1, date: form.date,
      });
      toast.success("Logged");
      setForm({ weight_lbs: "", reps: "", sets: "1", date: form.date });
      setCustomEx("");
      if (customEx.trim()) setExercise(customEx.trim());
      load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Failed"); }
    setSaving(false);
  };

  return (
    <div className="grid lg:grid-cols-3 gap-8">
      <div className="lg:col-span-1">
        <div className="border border-zinc-800 p-6">
          <div className="font-display text-2xl uppercase mb-4">Log a Lift</div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 block mb-2">Exercise</label>
              <Select value={exercise} onValueChange={setExercise}>
                <SelectTrigger data-testid="strength-exercise-select" className="rounded-none bg-black border-zinc-800 text-white h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none bg-[#0A0A0A] border-zinc-800 text-white">
                  {exercises.map(e => <SelectItem key={e} value={e} className="focus:bg-zinc-900 focus:text-white">{e}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input data-testid="strength-custom-exercise" placeholder="...or type new exercise" value={customEx} onChange={(e) => setCustomEx(e.target.value)} className="mt-2 rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-10" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field testid="strength-weight" label="Weight" value={form.weight_lbs} onChange={(v) => setForm({...form, weight_lbs: v})} />
              <Field testid="strength-reps" label="Reps" value={form.reps} onChange={(v) => setForm({...form, reps: v})} />
              <Field testid="strength-sets" label="Sets" value={form.sets} onChange={(v) => setForm({...form, sets: v})} />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 block mb-2">Date</label>
              <Input data-testid="strength-date" type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-11" />
            </div>
            <Button data-testid="strength-submit" type="submit" disabled={saving} className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-white uppercase tracking-[0.2em] h-11 text-xs w-full">{saving ? "Saving..." : "Log Lift"}</Button>
          </form>
        </div>
      </div>

      <div className="lg:col-span-2">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="font-display text-3xl uppercase">{exercise}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mt-1">Est. 1RM progression (Epley)</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Best e1RM</div>
            <div className="font-display text-4xl text-[#FF3B30]">{bestE1rm || "—"}<span className="text-zinc-500 text-base ml-1">lb</span></div>
          </div>
        </div>

        <div className="border border-zinc-800 bg-[#0A0A0A] p-4 h-80">
          {filtered.length < 2 ? (
            <div className="h-full grid place-items-center text-zinc-600 text-sm">Log at least 2 sessions for this lift to see progression.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={filtered} margin={{ left: 0, right: 12, top: 12, bottom: 0 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF3B30" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#FF3B30" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1F1F1F" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#52525B" tick={{ fontSize: 10 }} />
                <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 0 }} />
                <Area type="monotone" dataKey="e1rm" stroke="#FF3B30" strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Recent Sessions</div>
          <div className="border border-zinc-800">
            <div className="grid grid-cols-5 bg-[#121212] text-[10px] uppercase tracking-[0.25em] text-zinc-500 px-4 py-3">
              <div>Date</div><div>Weight</div><div>Reps</div><div>Sets</div><div>e1RM</div>
            </div>
            {[...filtered].reverse().slice(0, 8).map((r, i) => (
              <div key={i} className={`grid grid-cols-5 px-4 py-3 text-sm ${i % 2 ? "bg-[#0A0A0A]" : "bg-[#121212]"}`}>
                <div className="font-mono text-zinc-300">{r.date}</div>
                <div className="font-mono text-white">{r.weight}</div>
                <div className="font-mono text-zinc-300">{r.reps}</div>
                <div className="font-mono text-zinc-500">—</div>
                <div className="font-mono text-[#FF3B30]">{r.e1rm}</div>
              </div>
            ))}
            {filtered.length === 0 && <div className="p-6 text-center text-zinc-600 text-sm">No sessions yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 block mb-2">{label}</label>
      <Input data-testid={testid} type="number" step="0.1" value={value} onChange={(e) => onChange(e.target.value)} className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-11" />
    </div>
  );
}
